import { NullState, SearchInput } from '@mi-front/ui';
import { Box, Grid } from '@mui/material';
import { useMemo, useState } from 'react';
import { PermissionState } from '../../../types';
import { MinimumStation } from '@app/core/types/stations';

/** Permission rows that include a station (used for grouping in the grid). */
type PermissionStateWithStation = PermissionState & {
  readonly station: MinimumStation;
};

type BaseProps<T extends PermissionStateWithStation> = {
  readonly permissionsState: readonly T[];
  readonly filterPlaceholder: string;
  readonly emptyListMsg: React.ReactNode;
  readonly canModify: (pState: T) => boolean;
};

interface Props<T extends PermissionStateWithStation> extends BaseProps<T> {
  readonly filterFn: (pState: T, text: string) => boolean;
  readonly PermissionItemComponent: React.ComponentType<{
    readonly permissionState: T;
    readonly onUpdate?: (updatedState: T) => void;
  }>;
}

export function PermissionsView<T extends PermissionStateWithStation>(p: Props<T>) {
  const [filter, setFilter] = useState('');
  const [internalState, setInternalState] = useState(p.permissionsState);

  // Filter permissions based on filter text
  const filteredPermissions = useMemo(() => {
    if (!filter) {
      return internalState;
    }
    return internalState.filter((pState) => p.filterFn(pState, filter));
  }, [internalState, filter, p.filterFn]);

  // Group filtered permissions by station (each station becomes a "provider")
  const stationsMap = useMemo(() => {
    const map = new Map<
      string,
      {
        station: { id: string; name: string };
        permissions: T[];
      }
    >();

    filteredPermissions.forEach((pState) => {
      const stationId = pState.station?.id;

      if (stationId) {
        if (!map.has(stationId)) {
          map.set(stationId, {
            station: {
              id: stationId,
              name: pState.station.name,
            },
            permissions: [],
          });
        }
        map.get(stationId)!.permissions.push(pState);
      }
    });

    return map;
  }, [filteredPermissions]);

  const handleUpdate = (oldState: T, updatedState: T) => {
    const idx = internalState.indexOf(oldState);
    if (idx !== -1) {
      setInternalState([
        ...internalState.slice(0, idx),
        updatedState,
        ...internalState.slice(idx + 1),
      ]);
    }
  };

  if (filteredPermissions.length === 0) {
    return (
      <Box data-cy="permissions-view">
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <SearchInput
              data-cy="permissions-filter"
              value={filter}
              onSearch={setFilter}
              placeholder={p.filterPlaceholder}
              sx={{ mb: 2 }}
            />
          </Grid>
        </Grid>
        <NullState className="p-4">{p.emptyListMsg}</NullState>
      </Box>
    );
  }

  return (
    <Box data-cy="permissions-view">
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <SearchInput
            data-cy="permissions-filter"
            value={filter}
            onSearch={setFilter}
            placeholder={p.filterPlaceholder}
          />
        </Grid>
      </Grid>
      {Array.from(stationsMap.values()).map((stationData) => (
        <Grid key={stationData.station.id} container spacing={2} sx={{ marginBottom: 3 }}>
          {stationData.permissions.map((permissionState, index) => {
            const pState = permissionState as T;
            const Component = p.PermissionItemComponent;
            return (
              <Grid key={`${stationData.station.id}-${index}`} item xs={12}>
                <Component
                  permissionState={pState}
                  onUpdate={(updated) => {
                    handleUpdate(pState, updated);
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
      ))}
    </Box>
  );
}
